import {getTranslation} from "./helpers";
import {notifySupportTicketMutation} from './support-ticket-mutation-observer';
import utils from "../utils/utils";
import {userMessage} from "./user-message";
import {
    clearCloudAuthFromSession,
    readCloudAuthFromSession,
    writeCloudAuthToSession,
} from "./cloud-auth-storage";

const bootstrapAction = 'wl_get_support_ticket_admin_cloud_auth';
const protocolVersion = 1;
const authContextKey = `support-ticket-protocol-${protocolVersion}`;
const authRefreshSkewMs = 60 * 1000;
const requestTimeoutMs = 120 * 1000;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export const supportTicketReloadRequiredMessage = getTranslation('ticket_conflict_reopen');

let cloudAuth = null;
let cloudAuthPromise = null;

function ajaxPromise(ajaxSettings = {}) {
    return new Promise((resolve, reject) => {
        $.ajax({
            ...ajaxSettings,
            success: (response, textStatus, jqXHR) => resolve({response, textStatus, jqXHR}),
            error: (xhr, status, error) => reject({xhr, status, error}),
        });
    });
}

function normalizeStoredCloudAuth(authPayload = null) {
    if (!authPayload || typeof authPayload !== 'object') {
        return null;
    }

    const functionUrl = String(authPayload.functionUrl || '').trim();
    const authToken = String(authPayload.authToken || '').trim();
    const storedProtocolVersion = Number.parseInt(authPayload.protocolVersion, 10) || 0;
    const expiresAtMs = Number.parseInt(authPayload.expiresAtMs, 10) || 0;
    if (!functionUrl || !authToken || storedProtocolVersion !== protocolVersion || expiresAtMs <= 0) {
        return null;
    }

    return {
        functionUrl,
        authToken,
        protocolVersion: storedProtocolVersion,
        expiresAtMs,
    };
}

function normalizeCloudAuthResponse(response = null) {
    if (!response || response.success !== true || !response.data || typeof response.data !== 'object') {
        return null;
    }

    const functionUrl = String(response.data.function_url || '').trim();
    const authToken = String(response.data.auth_token || '').trim();
    const responseProtocolVersion = Number.parseInt(response.data.support_ticket_protocol_version, 10) || 0;
    const expiresAt = Number.parseInt(response.data.expires_at, 10) || 0;
    if (!functionUrl || !authToken || responseProtocolVersion !== protocolVersion || expiresAt <= 0) {
        return null;
    }

    return normalizeStoredCloudAuth({
        functionUrl,
        authToken,
        protocolVersion: responseProtocolVersion,
        expiresAtMs: expiresAt * 1000,
    });
}

function hasFreshCloudAuth() {
    const expiresAtMs = Number.parseInt(cloudAuth?.expiresAtMs, 10) || 0;
    return !!(
        cloudAuth?.functionUrl &&
        cloudAuth?.authToken &&
        expiresAtMs > Date.now() + authRefreshSkewMs
    );
}

function clearCloudAuth() {
    cloudAuth = null;
    clearCloudAuthFromSession({
        bootstrapAction,
        postId: utils.get_post_id(),
        nonce: utils.get_nonce(),
        contextKey: authContextKey,
    });
}

function isWpNonceFailure(error) {
    return Number.parseInt(error?.xhr?.status, 10) === 403 &&
        String(error?.xhr?.responseText || '').trim() === '-1';
}

function isCloudUnauthorized(error) {
    const status = Number.parseInt(error?.xhr?.status, 10) || 0;
    return status === 401 || status === 403;
}

function isRetryableCloudFailure(error) {
    const status = Number.parseInt(error?.xhr?.status, 10) || 0;
    return [0, 408, 429, 500, 502, 503, 504].includes(status);
}

async function fetchCloudAuth(forceRefresh = false) {
    if (forceRefresh) {
        clearCloudAuth();
    }

    if (!cloudAuth) {
        cloudAuth = readCloudAuthFromSession({
            bootstrapAction,
            postId: utils.get_post_id(),
            nonce: utils.get_nonce(),
            contextKey: authContextKey,
            normalizeAuthPayload: normalizeStoredCloudAuth,
        });
    }

    if (hasFreshCloudAuth()) {
        return cloudAuth;
    }

    if (cloudAuthPromise) {
        return cloudAuthPromise;
    }

    const requestAuth = async () => {
        const {response} = await ajaxPromise({
            url: utils.get_ajax_url(),
            type: 'POST',
            dataType: 'json',
            wl_skip_nonce_retry: true,
            data: {
                action: bootstrapAction,
                _wpnonce: utils.get_nonce(),
                post_id: utils.get_post_id(),
            },
        });
        const authPayload = normalizeCloudAuthResponse(response);
        if (!authPayload) {
            throw new Error('Failed to initialize support ticket access.');
        }

        cloudAuth = authPayload;
        writeCloudAuthToSession({
            bootstrapAction,
            postId: utils.get_post_id(),
            nonce: utils.get_nonce(),
            contextKey: authContextKey,
            authPayload,
            normalizeAuthPayload: normalizeStoredCloudAuth,
        });

        return cloudAuth;
    };

    cloudAuthPromise = (async () => {
        try {
            return await requestAuth();
        } catch (error) {
            if (isWpNonceFailure(error)) {
                await utils.fetch_fresh_nonce(true);
                return requestAuth();
            }

            if (isCloudUnauthorized(error)) {
                clearCloudAuth();
            }

            throw error;
        } finally {
            cloudAuthPromise = null;
        }
    })();

    return cloudAuthPromise;
}

function normalizeMutationResponse(response = null, mutation = null) {
    const firebaseId = String(response?.firebase_id || '').trim().toLowerCase();
    const mutationId = String(response?.mutation_id || '').trim().toLowerCase();
    const revision = Number.parseInt(response?.revision, 10);
    const ticketNumber = Number.parseInt(response?.ticket_number, 10);
    const hasReloadRequired = Object.prototype.hasOwnProperty.call(response || {}, 'reload_required');
    if (
        response?.success !== true ||
        response?.action !== mutation?.action ||
        mutationId !== mutation?.mutation_id ||
        !uuidPattern.test(firebaseId) ||
        (mutation?.firebase_id && firebaseId !== mutation.firebase_id) ||
        !Number.isInteger(revision) ||
        revision <= 0 ||
        !Number.isInteger(ticketNumber) ||
        ticketNumber <= 0 ||
        (hasReloadRequired && typeof response.reload_required !== 'boolean') ||
        response?.live_update_context?.post_type !== 'support_ticket'
    ) {
        throw new Error('Support ticket was saved, but the Cloud response was invalid.');
    }

    return {
        ...response,
        firebase_id: firebaseId,
        mutation_id: mutationId,
        revision,
        ticket_number: ticketNumber,
        reload_required: response.reload_required === true,
    };
}

async function sendMutation(authState, mutation) {
    return ajaxPromise({
        url: authState.functionUrl,
        type: 'POST',
        dataType: 'json',
        contentType: 'application/json; charset=UTF-8',
        crossDomain: true,
        timeout: requestTimeoutMs,
        data: JSON.stringify({
            ...mutation,
            auth_token: authState.authToken,
        }),
    });
}

async function sendMutationWithNetworkRetry(authState, mutation) {
    try {
        return await sendMutation(authState, mutation);
    } catch (error) {
        if (!isRetryableCloudFailure(error)) {
            throw error;
        }

        return sendMutation(authState, mutation);
    }
}

export function createSupportTicketMutationId() {
    return window.crypto.randomUUID().toLowerCase();
}

export function isSupportTicketFirebaseId(value = '') {
    return uuidPattern.test(String(value || '').trim().toLowerCase());
}

export function isSupportTicketMutationOutcomeUncertain(error) {
    if (!error?.xhr) {
        return true;
    }

    return isRetryableCloudFailure(error);
}

async function requestValidatedSupportTicketMutation(mutation) {
    // Main writes always use current WordPress field definitions. The submitted
    // config hash makes an already-open popup fail closed after a config change.
    let authState = await fetchCloudAuth(true);

    try {
        const {response} = await sendMutationWithNetworkRetry(authState, mutation);
        return normalizeMutationResponse(response, mutation);
    } catch (error) {
        if (!isCloudUnauthorized(error)) {
            throw error;
        }

        authState = await fetchCloudAuth(true);
        const {response} = await sendMutationWithNetworkRetry(authState, mutation);
        return normalizeMutationResponse(response, mutation);
    }
}

export async function requestSupportTicketMutation(mutation) {
    await notifySupportTicketMutation('before', mutation);
    let response;
    try {
        response = await requestValidatedSupportTicketMutation(mutation);
    } catch (error) {
        await notifySupportTicketMutation('failed', mutation, error);
        throw error;
    }
    // Observer work must stay outside transport retries: a successful ticket
    // must never be created again because its email association failed.
    await notifySupportTicketMutation('saved', mutation, response);
    return response;
}

export function getSupportTicketMutationErrorMessage(error) {
    const status = Number.parseInt(error?.xhr?.status, 10) || 0;
    const responseMessage = String(
        userMessage(error?.xhr?.responseJSON) ||
        userMessage(error?.xhr?.responseJSON?.data) ||
        ''
    ).trim();

    if (status === 409) {
        return responseMessage || getTranslation('ticket_conflict_reload');
    }

    if (status === 401 || status === 403) {
        return getTranslation('ticket_session_expired');
    }

    return responseMessage || error?.message || getTranslation('ticket_save_error');
}
